import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, ScrollText, Calendar } from 'lucide-react';
import { SubscriptionState } from '../types';
import OptimizedImageByName from './common/OptimizedImageByName';

interface ServiceSwitcherProps {
  isLoading?: boolean;
  subscriptions?: SubscriptionState[];
}

export default function ServiceSwitcher({ isLoading = false, subscriptions = [] }: ServiceSwitcherProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);

  // Determine current active service based on pathname
  const getCurrentService = () => {
    if (location.pathname.includes('/flowers')) return 'flowers';
    if (location.pathname.includes('/fruits')) return 'fruits';
    if (location.pathname.includes('/cat-grass')) return 'cat-grass';
    return 'flowers'; // default
  };

  const currentService = getCurrentService();

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 100); // Show compact version after scrolling 100px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Service options with images
  const services = [
    { 
      key: 'flowers', 
      label: t('common.flowers', 'Flowers'), 
      path: '/flowers',
      bgColor: 'bg-yellow-100'
    },
    { 
      key: 'fruits', 
      label: t('common.fruits', 'Fruits'), 
      path: '/fruits',
      bgColor: 'bg-orange-100'
    },
    { 
      key: 'cat-grass', 
      label: t('common.navigation.catGrass', 'Cat Grass'), 
      path: '/cat-grass',
      bgColor: 'bg-green-100'
    }
  ];

  const handleServiceSwitch = (service: typeof services[0]) => {
    navigate(service.path);
  };

  // Determine if there are any important notifications for the user
  const hasNotifications = false; // Set this to true only when you have actual notifications

  return (
    <div className={`bg-white transition-all duration-300 ${isScrolled ? 'fixed top-0 left-0 right-0 z-50 shadow-md' : ''}`}>
      {/* Service Switcher Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ display: 'none' }}>
        <div className="flex items-center">
          <h1 className="font-semibold text-xl text-black">
            Piika
          </h1>
        </div>
        
        {/* Right side icons */}
        <div className="flex items-center space-x-4">
          {/* User profile icon - always visible */}
          <div className="relative" onClick={() => navigate('/me')} style={{ cursor: 'pointer' }}>
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-100">
              <User size={20} className="text-amber-700" />
            </div>
          </div>
          
          {/* Notification icons if user has subscription */}
          {!isLoading && subscriptions.length > 0 && (
            <>
              <div className="relative" onClick={() => navigate('/delivery-activity')} style={{ cursor: 'pointer' }}>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-100">
                  <ScrollText size={20} className="text-amber-700" />
                </div>
              </div>
              <div className="relative" onClick={() => navigate('/subscription-management')} style={{ cursor: 'pointer' }}>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-100">
                  <Calendar size={20} className="text-amber-700" />
                </div>
                {/* Only show the notification badge when there are actual notifications */} 
                {hasNotifications && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full border-2 border-white"></span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Service Tabs */}
      <div className={`px-4 border-b border-gray-100 transition-all duration-300 ${isScrolled ? 'py-0' : ''}`}>
        <div className="flex bg-white justify-center items-center gap-6">
          {services.map((service, index) => {
            const isActive = currentService === service.key;
            
            return (
              <button
                key={service.key}
                onClick={() => handleServiceSwitch(service)}
                className={`flex flex-col items-center justify-center transition-all duration-300 bg-white opacity-100 border-none relative ${isScrolled ? 'px-4 py-2' : 'px-2 py-3'}`}
              >
                {!isScrolled && (
                  <>
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center`}>
                      <OptimizedImageByName 
                        imageName={
                          service.key === 'flowers' ? 'servicesFlower' : 
                          service.key === 'fruits' ? 'servicesFruit' : 
                          'servicesCatGrass' // Now using dedicated cat grass service icon
                        } 
                        alt={service.label} 
                        className={service.key === 'cat-grass' ? 'h-16 w-16 object-contain' : 'h-14 w-14 object-contain'} 
                      />
                    </div>
                    {(service.key === 'fruits' || service.key === 'cat-grass') && (
                      <div className={`absolute top-2 -right-6 text-white text-xs px-1.5 py-0.5 rounded-md ${
                        service.key === 'fruits' ? 'bg-orange-500' : 'bg-green-500'
                      }`}>
                        NEW
                      </div>
                    )}
                  </>
                )}
                <span className={`${isScrolled ? 'text-sm' : 'text-xs'} ${isActive ? 'text-black font-medium' : 'text-gray-400'} transition-all duration-300`}>
                  {service.label}
                </span>
                {isActive && (
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-black rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
} 